import { useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Collapse, Drawer, Space, Spin } from 'antd';
import { AGE_GROUPS, type AgeGroup } from '../../../constants/ageGroup';
import { GENDERS, type Gender } from '../../../constants/gender';
import {
  ACTIVITY_TYPES,
  type ActivityType,
} from '../../../constants/activityType';
import { TRAVEL_MODES, type TravelMode } from '../../../constants/travelMode';
import {
  useDataFilter,
  type AttributeFilterType,
} from '../../../hooks/SimViewer/useDataFilter';
import { useMetaData } from '../../../hooks/data/useMetaData';
import {
  useAttributeAggregationData,
  type AttributeAggregationDimension,
} from '../../../hooks/data/useAttributeAggregationData';
import { downloadAsCsv } from '../../../utils/file';
import { toPascal } from '../../../utils/case';
import { BarChart, type BarItem } from '../Chart/BarChart';
import { FilterIcon } from '../../FilterIcon';
import { ActionButtons } from './ActionButtons';
import { AttributeAggregationComparison } from './AttributeAggregationComparison';

const FILTER_KEYS = {
  gender: 'genderIds',
  age: 'ageGroups',
  nationality: 'nationalities',
  end_activity_type: 'endActivityTypes',
  longest_distance_mode: 'longestDistanceModes',
} as const satisfies Record<AttributeAggregationDimension, AttributeFilterType>;

type Props = {
  dimension: AttributeAggregationDimension;
  icon: ReactNode;
  label: string;
  extra?: ReactNode;
};

const AttributePanel = ({
  dimension,
  icon,
  label,
  extra,
  ...props
}: Props): ReactNode => {
  const { dataFilter } = useDataFilter();

  return (
    <Collapse.Panel
      key={dimension}
      header={
        <Space>
          {icon}
          {label}
          {extra}
          {dataFilter[FILTER_KEYS[dimension]].length > 0 && <FilterIcon />}
        </Space>
      }
      {...props}
    >
      <AttributePanelContent dimension={dimension} label={label} />
    </Collapse.Panel>
  );
};

const AttributePanelContent = ({
  dimension,
  label,
}: Pick<Props, 'dimension' | 'label'>): ReactNode => {
  const { t } = useTranslation();
  const { currentDataMeta } = useMetaData();
  const { dataFilter, addAttributeFilter, deleteAttributeFilter } =
    useDataFilter();
  const { isLoading, aggregations } = useAttributeAggregationData(dimension);

  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);

  const barChartData = useMemo((): BarItem[] => {
    switch (dimension) {
      case 'gender':
        return GENDERS.map((gender: Gender): BarItem => {
          return {
            key: gender,
            label: gender === '1' ? t('genders.male') : t('genders.female'),
            value: aggregations.get(gender) ?? 0,
          };
        });
      case 'age':
        return AGE_GROUPS.filter((ageGroup: AgeGroup): boolean => {
          if (currentDataMeta?.type === 'gad_standard') {
            return !['10', '70'].includes(ageGroup);
          }
          return true;
        }).map((ageGroup: AgeGroup): BarItem => {
          return {
            key: ageGroup,
            label:
              currentDataMeta?.type === 'gad_standard' && ageGroup === '60'
                ? t(`ageGroups.60s+`)
                : t(`ageGroups.${ageGroup}s`),
            value: aggregations.get(ageGroup) ?? 0,
          };
        });
      case 'nationality':
        return Array.from(aggregations)
          .map(([countryCode, value]: [string, number]): BarItem => {
            return {
              key: countryCode,
              label: t(`countries.${countryCode}`, {
                defaultValue: t('countries.ZZZ'),
              }),
              value: value,
            };
          })
          .sort((item1: BarItem, item2: BarItem): number => {
            // 集計値の大きい順にソートする
            return item2.value - item1.value;
          });
      case 'end_activity_type':
        return ACTIVITY_TYPES.map((activityType: ActivityType): BarItem => {
          return {
            key: activityType,
            label: t(`activityType.${activityType}`),
            value: aggregations.get(activityType) ?? 0,
          };
        }).sort((item1: BarItem, item2: BarItem): number => {
          // 集計値の大きい順にソートしながら、その他を末尾にする
          if (item1.key === 'others') {
            return 1;
          }
          if (item2.key === 'others') {
            return -1;
          }
          return item2.value - item1.value;
        });
      case 'longest_distance_mode':
        return TRAVEL_MODES.filter((mode: TravelMode): boolean => {
          // 移動手段については集計が存在するもののみを対象とする
          return aggregations.has(mode);
        })
          .map((mode: TravelMode): BarItem => {
            return {
              key: mode,
              label: t(`travelMode.${mode}`),
              value: aggregations.get(mode) ?? 0,
            };
          })
          .sort((item1: BarItem, item2: BarItem): number => {
            // 集計値の大きい順にソートしながら、その他を末尾にする
            if (item1.key === 'other') {
              return 1;
            }
            if (item2.key === 'other') {
              return -1;
            }
            return item2.value - item1.value;
          });
    }
  }, [dimension, aggregations, currentDataMeta?.type, t]);

  return (
    <Spin spinning={isLoading}>
      <BarChart
        data={barChartData}
        barWidth={
          ['gender', 'longest_distance_mode'].includes(dimension)
            ? 'wide'
            : 'normal'
        }
        onBarItemClick={(key: string): void => {
          if (dataFilter[FILTER_KEYS[dimension]].includes(key)) {
            deleteAttributeFilter(FILTER_KEYS[dimension], key);
          } else {
            addAttributeFilter(FILTER_KEYS[dimension], key);
          }
        }}
        selectedLabels={dataFilter[FILTER_KEYS[dimension]]}
        noDataMessage={
          ['gender', 'age', 'nationality'].includes(dimension)
            ? t('message.noAgentFound')
            : t('message.noTripFound')
        }
        unit={t('unit.agent')}
      />
      <ActionButtons
        onOpenComparison={(): void => {
          setIsDrawerOpen(true);
        }}
        onExportCsv={(withBom: boolean): void => {
          downloadAsCsv(
            [
              [
                label,
                `${t('agentCount')}${t('unit.agent') ? ` [${t('unit.agent')}]` : ''}`,
              ],
              ...barChartData.map((row): (string | number)[] => {
                return [row.label, row.value];
              }),
            ],
            `${currentDataMeta?.name ?? ''}_${toPascal(label)}.csv`,
            { withBom: withBom }
          );
        }}
        disabled={isLoading}
      />
      <Drawer
        title={t('barChartTitle', { target: label })}
        placement="right"
        width="80%"
        destroyOnClose
        open={isDrawerOpen}
        onClose={(): void => {
          setIsDrawerOpen(false);
        }}
      >
        <AttributeAggregationComparison
          dimension={dimension}
          selectedLabels={dataFilter[FILTER_KEYS[dimension]]}
          barWidth={
            ['gender', 'longest_distance_mode'].includes(dimension)
              ? 'wide'
              : 'normal'
          }
          noDataMessage={
            ['gender', 'age', 'nationality'].includes(dimension)
              ? t('message.noAgentFound')
              : t('message.noTripFound')
          }
          unit={t('unit.agent')}
        />
      </Drawer>
    </Spin>
  );
};

export { AttributePanel };
